import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import PublicHeader from '../components/layout/PublicHeader';
import PublicFooter from '../components/layout/PublicFooter';
import config from '../config';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <Link
              to="/home"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-3 sm:mb-4 text-sm sm:text-base"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
            <div className="flex items-center mb-3 sm:mb-4">
              <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-primary-600 mr-2 sm:mr-3 flex-shrink-0" />
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Privacy Policy</h1>
            </div>
            <p className="text-sm sm:text-base text-gray-600">
              Last updated: March 3, 2025
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-sm sm:prose-lg max-w-none">
            <section className="mb-6 sm:mb-8">
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 sm:mb-4">1. Introduction</h2>
              <p className="text-sm sm:text-base text-gray-700 mb-3 sm:mb-4">
                {config.appName} ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform and services.
              </p>
              <p className="text-gray-700 mb-4">
                Please read this Privacy Policy carefully. By using our Service, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2.1 Information You Provide</h3>
              <p className="text-gray-700 mb-4">We collect information that you provide directly to us, including:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Account Information:</strong> Name, email address, password, company name, and other registration details</li>
                <li><strong>Document Content:</strong> Documents you upload to the Service, including PDFs, Word documents, text files, and other supported formats</li>
                <li><strong>Usage Data:</strong> Queries, questions, and interactions with the AI service</li>
                <li><strong>Contact Information:</strong> Information provided when you contact us for support or evaluation access</li>
                <li><strong>Payment Information:</strong> Billing details and payment information (processed through secure third-party payment processors)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2.2 Automatically Collected Information</h3>
              <p className="text-gray-700 mb-4">We automatically collect certain information when you use our Service:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Log Data:</strong> IP address, browser type, operating system, access times, and pages viewed</li>
                <li><strong>Usage Analytics:</strong> Features used, time spent on the Service, and interaction patterns</li>
                <li><strong>Device Information:</strong> Device type, device identifiers, and mobile network information</li>
                <li><strong>Cookies and Tracking Technologies:</strong> Information collected through cookies, web beacons, and similar technologies</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">We use the collected information for various purposes:</p>
              <p className="text-gray-700 mb-4">
                <strong>AI and automated processing:</strong> Document content and your queries are processed using AI/LLM technology to provide search, summarization, and answers. We do not use your document content or queries to train general-purpose AI models. For private AI/LLM deployments, processing occurs entirely within your infrastructure.
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>To provide, maintain, and improve our Service</li>
                <li>To process your documents and respond to your queries using AI technology</li>
                <li>To manage your account and provide customer support</li>
                <li>To send you administrative information, updates, and security alerts</li>
                <li>To process payments and manage subscriptions</li>
                <li>To detect, prevent, and address technical issues and security threats</li>
                <li>To comply with legal obligations and enforce our Terms and Conditions</li>
                <li>To analyze usage patterns and improve user experience</li>
                <li>To communicate with you about products, services, and promotional offers (with your consent)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Storage and Security</h2>
              <p className="text-gray-700 mb-4">
                We implement appropriate technical and organizational security measures to protect your information against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure authentication and access controls</li>
                <li>Regular security assessments and updates</li>
                <li>Limited access to personal information on a need-to-know basis</li>
                <li>Secure data centers and infrastructure</li>
              </ul>
              <p className="text-gray-700 mb-4">
                However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Third-Party Integrations and Data Sources</h2>
              <p className="text-gray-700 mb-4">
                The Service may allow you to connect third-party data sources (e.g., Google Drive, Google Search Console, or other connectors). When you connect such services:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>We receive only the data and permissions necessary to provide the feature (e.g., file metadata and content you choose to sync)</li>
                <li>Your use of third-party services is subject to those providers' terms and privacy policies</li>
                <li>We do not sell or share data from these integrations with other third parties except as described in this policy</li>
                <li>You can disconnect integrations at any time through your account or data source settings</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Private AI/LLM Services</h2>
              <p className="text-gray-700 mb-4">
                For organizations using our private AI/LLM deployment services, your documents and queries are processed within your own private infrastructure. This ensures that:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Your data never leaves your private environment</li>
                <li>No information is shared with public AI services</li>
                <li>You maintain complete control over your data</li>
                <li>Compliance with strict data residency and privacy requirements</li>
              </ul>
              <p className="text-gray-700 mb-4">
                For the hosted demonstration service, data is processed in our secure cloud infrastructure with the same security standards.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Information Sharing and Disclosure</h2>
              <p className="text-gray-700 mb-4">We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:</p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">7.1 Service Providers</h3>
              <p className="text-gray-700 mb-4">
                We may share information with trusted third-party service providers who assist us in operating our Service, conducting business, or serving users, such as:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Cloud hosting and infrastructure providers</li>
                <li>Payment processors</li>
                <li>Email service providers</li>
                <li>Analytics and monitoring services</li>
              </ul>
              <p className="text-gray-700 mb-4">These service providers are contractually obligated to protect your information and use it only for the purposes we specify.</p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">7.2 Legal Requirements</h3>
              <p className="text-gray-700 mb-4">
                We may disclose your information if required by law or in response to valid requests by public authorities (e.g., court orders, government agencies).
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">7.3 Business Transfers</h3>
              <p className="text-gray-700 mb-4">
                In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction, subject to the same privacy protections.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Data Retention</h2>
              <p className="text-gray-700 mb-4">
                We retain your information for as long as necessary to provide the Service, comply with legal obligations, resolve disputes, and enforce our agreements. Specifically:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Account information is retained while your account is active</li>
                <li>Documents and queries are retained according to your subscription plan and settings</li>
                <li>We may retain certain information after account deletion as required by law or for legitimate business purposes</li>
                <li>You can request deletion of your data at any time, subject to legal retention requirements</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Your Rights and Choices</h2>
              <p className="text-gray-700 mb-4">Depending on your location, you may have certain rights regarding your personal information:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Access:</strong> Request access to your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
                <li><strong>Opt-out:</strong> Opt-out of certain data processing activities, such as marketing communications</li>
                <li><strong>Account Settings:</strong> Update your account information and preferences through your account settings</li>
              </ul>
              <p className="text-gray-700 mb-4">
                To exercise these rights, please contact us through our <Link to="/contact" className="text-primary-600 hover:text-primary-700 underline">Contact Us</Link> page.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 mb-4">
                We use cookies and similar tracking technologies to track activity on our Service and store certain information. Cookies are files with a small amount of data that may include an anonymous unique identifier.
              </p>
              <p className="text-gray-700 mb-4">Types of cookies we use:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Essential Cookies:</strong> Required for the Service to function properly</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how users interact with the Service</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
              </ul>
              <p className="text-gray-700 mb-4">
                You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Children's Privacy</h2>
              <p className="text-gray-700 mb-4">
                Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. International Data Transfers</h2>
              <p className="text-gray-700 mb-4">
                Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. We take appropriate measures to ensure that your information receives an adequate level of protection.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically for any changes.
              </p>
              <p className="text-gray-700 mb-4">
                Material changes will be communicated to you via email or through a prominent notice on our Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us through our <Link to="/contact" className="text-primary-600 hover:text-primary-700 underline">Contact Us</Link> page.
              </p>
              <p className="text-gray-700 mb-4">
                For privacy-related requests or to exercise your rights, please include "Privacy Request" in your message subject line.
              </p>
            </section>
          </div>

          {/* Footer Actions */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <Link
              to="/home"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
};

export default PrivacyPolicy;

